import { useState } from 'react'
import PropTypes from 'prop-types'
import Button from '@/shared/components/UI/Button'
import mixpanel from '@/utils/mixpanel'
import { useUserStore } from '@/stores/user/userStore'

const COMPETITORS = [
  {
    id: 'intercom',
    name: 'Intercom',
    counter:
      'Intercom is a broad customer messaging platform. Dante AI is built around AI agents trained on your own content, so answers stay accurate to your business.',
  },
  {
    id: 'zendesk',
    name: 'Zendesk',
    counter:
      'Zendesk AI is aimed at large enterprise teams. Dante AI focuses on flexible AI chatbots without the heavy enterprise overhead.',
  },
  {
    id: 'tidio',
    name: 'Tidio',
    counter:
      'Tidio is great for basic live chat. If you want AI that handles richer questions and learns from your docs and site, Dante AI goes further.',
  },
  {
    id: 'crisp',
    name: 'Crisp',
    counter:
      'Crisp works well for small teams and simple chat. Dante AI offers deeper AI training on your materials and stronger automation as you grow.',
  },
  {
    id: 'other',
    name: 'Something else',
    counter:
      "We'd love to know more. Whatever you're moving to, we're happy to show how Dante AI compares and what might work best for you.",
  },
]

const StepCompetitor = ({ onAcceptDiscount, onDeclineOffer, isCancelLoading, mixpanelTargetTier }) => {
  const [selectedId, setSelectedId] = useState(null)
  const currentTier = useUserStore((s) => s.user?.tier_type)

  const selected = COMPETITORS.find((c) => c.id === selectedId) || null
  const canSeeOffer = Boolean(selectedId)

  return (
    <div className="flex flex-col gap-v3-md">
      <p className="text-v3-h6 font-bold text-v3-black-max">Which tool are you switching to?</p>

      {/* Competitor pills */}
      <div className="flex flex-wrap gap-v3-xs" role="group" aria-label="Competitor you are switching to">
        {COMPETITORS.map((competitor) => {
          const isSelected = selectedId === competitor.id
          return (
            <button
              key={competitor.id}
              onClick={() => setSelectedId(isSelected ? null : competitor.id)}
              className={`px-v3-sm py-v3-xs rounded-full border text-[14px] font-medium cursor-pointer transition-colors ${
                isSelected
                  ? 'border-v3-purple-primary bg-v3-purple-bg text-v3-purple-primary'
                  : 'border-v3-card-border bg-white text-v3-black-primary hover:border-v3-purple-primary'
              }`}
            >
              {competitor.name}
            </button>
          )
        })}
      </div>

      {!canSeeOffer && (
        <p className="text-[12px] text-v3-black-secondary -mt-v3-xs">
          Pick the tool you&apos;re moving to so we can show a tailored note and the retention offer.
        </p>
      )}

      {/* Counter message */}
      <div
        className={`transition-all overflow-hidden ${
          selected ? 'opacity-100 max-h-40' : 'opacity-0 max-h-0'
        }`}
      >
        {selected && (
          <div className="rounded-v3-md border border-v3-card-border bg-v3-black-subtle p-v3-sm">
            <p className="text-v3-black-primary text-v3-h9 italic">{selected.counter}</p>
          </div>
        )}
      </div>

      {/* Offer teaser */}
      <div className="rounded-v3-md bg-v3-purple-bg border border-v3-purple-primary p-v3-sm text-center">
        <p className="text-v3-black-max text-v3-h9 font-medium">
          Before you go - we have a special offer that might change your mind.
        </p>
      </div>

      {/* Buttons */}
      <div className="flex flex-col gap-v3-xs w-full items-center">
        <Button
          variant="primary"
          size="lg"
          label="See the offer"
          onClick={() => {
            mixpanel.track('Downgrade Flow V2 Competitor See Offer Clicked', {
              competitor_id: selectedId,
              current_tier: currentTier,
              target_tier: mixpanelTargetTier,
            })
            onAcceptDiscount()
          }}
          disabled={!canSeeOffer}
        />
        <Button
          variant="transparent"
          label="No thanks - cancel my subscription"
          onClick={() => {
            mixpanel.track('Downgrade Flow V2 Competitor Cancel Clicked', {
              competitor_id: selectedId,
              current_tier: currentTier,
              target_tier: mixpanelTargetTier,
            })
            onDeclineOffer()
          }}
          loading={isCancelLoading}
          loadingLabel="Cancelling..."
          disabled={isCancelLoading}
        />
      </div>
    </div>
  )
}

StepCompetitor.propTypes = {
  onAcceptDiscount: PropTypes.func.isRequired,
  onDeclineOffer: PropTypes.func.isRequired,
  isCancelLoading: PropTypes.bool,
  mixpanelTargetTier: PropTypes.string,
}

StepCompetitor.defaultProps = {
  isCancelLoading: false,
  mixpanelTargetTier: null,
}

export default StepCompetitor
